import AIStateFilter from '@/pages/AiHistory/components/AIStateFilter.js';
import SentimentFilter from '@/pages/AiHistory/components/SentimentFilter.js';
import TypeFilter from '@/pages/AiHistory/components/TypeFilter';
import { Col, Flex, Row } from 'antd';
import React from 'react';
import AppliedFilterPills from '../../Filters/AppliedFilterPills.js';
import FilterDropdowns from '../../Filters/FilterDropdowns.js';
import type { useShipmentFiltersPanelController } from '../hooks/useShipmentFiltersPanelController';
import ShipmentSaveFilterSetModal from './ShipmentSaveFilterSetModal';
import ShipmentSearchControl from './ShipmentSearchControl';
import ViewFiltersModal from './ViewFilterModal';
import { ControlTowerView } from '../index.js';

const styles: Record<string, React.CSSProperties> = {
  row: { minWidth: 0 },
  toolbarFlex: {
    width: '100%',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '12px',
  },
  leftWrapFlex: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'nowrap',
  },
  filterDropdownWrapper: { position: 'relative' },
};

type ShipmentFiltersPanelViewProps = ReturnType<
  typeof useShipmentFiltersPanelController
>;

const ShipmentFiltersPanelView: React.FC<ShipmentFiltersPanelViewProps> = ({
  addFilterBgColor,
  addFilterFontSize,
  savedFilterKey,
  modalMode,
  showSearch,
  isSearchVisible,
  isCompactSearch,
  searchPopoverOpen,
  searchQuery,
  appliedSearchQuery,
  setSearchQuery,
  setSearchPopoverOpen,
  handleSearch,
  handleCloseSearch,
  handleResetSearch,
  handlePopoverClose,
  handleSearchIconClick,
  dropdownMaxHeight,
  openedFromPill,
  submenuAnchor,
  appliedFilters,
  menu,
  isFilterOpen,
  setIsFilterOpen,
  secondFilterDisplay,
  setSecondFilterDisplay,
  filterBy,
  isDatePicker,
  calendarMenu,
  renderSubMenu,
  popupContainer,
  showAiStateFilter,
  setAIStateSelected,
  aiStateSections,
  aiStateSelected = [],
  showSentimentFilter,
  setSentimentSelected,
  sentimentSections,
  sentimentSelected = [],
  typeSelected = [],
  setTypeSelected,
  showAppliedFilterPills,
  hasSelectionFilters,
  hasAnyActiveFilter,
  lookups,
  showSaveFilterSetLink,
  setEditingId,
  openSubmenuAt,
  setOpenedFromPill,
  handleResetAllFilters,
  removeAppliedFilter,
  setSecondaryFiltersDDL,
  setSecondaryFiltersSource,
  setOptionsLoading,
  setIsDatePicker,
  filterTitle,
  setFilterTitle,
  setFilterBy,
  filterArray,
  handleOpenSaveFilterSet,
  isFilterSetSaved,
  useInternalSaveFilterSetModal,
  savePresetOpen,
  closeSavePresetModal,
  presets,
  selectedPresetId,
  handleSavePreset,
  setLastSavedFiltersSignature,
  filterMenuItems,
  isShipmentToolbarFilter,
  viewFiltersTab,
  viewFiltersModalOpen,
  handleCloseViewFiltersModal,
}) => {
  const envView = process.env.REACT_APP_CONTROL_TOWER_VIEW as ControlTowerView;

  return (
    <>
      {showSearch && isShipmentToolbarFilter && (
        <ShipmentSearchControl
          isSearchVisible={isSearchVisible}
          isCompactSearch={isCompactSearch}
          searchPopoverOpen={searchPopoverOpen}
          searchQuery={searchQuery}
          appliedSearchQuery={appliedSearchQuery}
          savedFilterKey={savedFilterKey}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          handleCloseSearch={handleCloseSearch}
          handleResetSearch={handleResetSearch}
          handlePopoverClose={handlePopoverClose}
          handleSearchIconClick={handleSearchIconClick}
          setSearchPopoverOpen={setSearchPopoverOpen}
          tableView={envView !== 'list'}
        />
      )}
      <Row
        gutter={16}
        style={
          savedFilterKey !== 'ai_history' && !modalMode ? styles.row : undefined
        }
      >
        <Col span={24}>
          <Flex align='center' gap={16} style={styles.toolbarFlex}>
            <Flex wrap gap={8} align='center' style={styles.leftWrapFlex}>
              {showSearch && !isShipmentToolbarFilter && (
                <ShipmentSearchControl
                  isSearchVisible={isSearchVisible}
                  isCompactSearch={isCompactSearch}
                  searchPopoverOpen={searchPopoverOpen}
                  searchQuery={searchQuery}
                  appliedSearchQuery={appliedSearchQuery}
                  savedFilterKey={savedFilterKey}
                  setSearchQuery={setSearchQuery}
                  handleSearch={handleSearch}
                  handleCloseSearch={handleCloseSearch}
                  handleResetSearch={handleResetSearch}
                  handlePopoverClose={handlePopoverClose}
                  handleSearchIconClick={handleSearchIconClick}
                  setSearchPopoverOpen={setSearchPopoverOpen}
                />
              )}
              {/* eslint-disable-next-line react/forbid-dom-props -- layout styles predate the strict rule; to move into CSS */}
              <div style={styles.filterDropdownWrapper}>
                <FilterDropdowns
                  dropdownMaxHeight={dropdownMaxHeight}
                  isAiHistory={savedFilterKey === 'ai_history'}
                  openedFromPill={openedFromPill}
                  submenuAnchor={submenuAnchor ?? undefined}
                  appliedFilters={appliedFilters}
                  menu={menu}
                  isFilterOpen={isFilterOpen}
                  setIsFilterOpen={setIsFilterOpen}
                  secondFilterDisplay={secondFilterDisplay}
                  setSecondFilterDisplay={setSecondFilterDisplay}
                  filterBy={filterBy}
                  isDatePicker={isDatePicker}
                  calendarMenu={calendarMenu}
                  renderSubMenu={renderSubMenu}
                  zIndex={true}
                  showAddFilter={true}
                  addFilterBgColor={addFilterBgColor}
                  addFilterFontSize={addFilterFontSize}
                  getPopupContainer={() =>
                    modalMode ? document.body : popupContainer || document.body
                  }
                  pillsDropdownMenuItems={filterMenuItems}
                  isShipmentToolbarFilter={isShipmentToolbarFilter}
                  savedFilterKey={savedFilterKey}
                />
              </div>

              <Flex align='center' wrap>
                <Flex align='center' gap={12} wrap={false}>
                  {showAiStateFilter && setAIStateSelected && (
                    <AIStateFilter
                      mode='dropdown'
                      sections={aiStateSections ?? []}
                      selectedStates={aiStateSelected}
                      onChange={setAIStateSelected}
                    />
                  )}

                  {showSentimentFilter && setSentimentSelected && (
                    <SentimentFilter
                      mode='dropdown'
                      sections={sentimentSections ?? []}
                      selectedSentiments={sentimentSelected ?? []}
                      onChange={setSentimentSelected}
                    />
                  )}

                  {setTypeSelected && (
                    <TypeFilter
                      mode='dropdown'
                      selectedTypes={typeSelected ?? []}
                      onChange={setTypeSelected}
                    />
                  )}
                </Flex>

                {(showAiStateFilter || showSentimentFilter) &&
                  (aiStateSelected?.length > 0 ||
                    sentimentSelected?.length > 0 ||
                    typeSelected?.length > 0) && (
                    <Flex
                      align='center'
                      gap={8}
                      wrap={false}
                      style={{ marginLeft: 8 }}
                    >
                      {aiStateSelected?.length > 0 && (
                        <AIStateFilter
                          mode='pill'
                          sections={aiStateSections ?? []}
                          selectedStates={aiStateSelected}
                          onChange={(v) => setAIStateSelected?.(v)}
                        />
                      )}

                      {sentimentSelected?.length > 0 && (
                        <SentimentFilter
                          mode='pill'
                          sections={sentimentSections ?? []}
                          selectedSentiments={sentimentSelected}
                          onChange={(v) => setSentimentSelected?.(v)}
                        />
                      )}

                      {typeSelected?.length > 0 && (
                        <TypeFilter
                          mode='pill'
                          selectedTypes={typeSelected}
                          onChange={(v) => setTypeSelected?.(v)}
                        />
                      )}
                    </Flex>
                  )}
              </Flex>

              {showAppliedFilterPills && (
                <AppliedFilterPills
                  lookups={lookups}
                  showSaveFilterSetLink={showSaveFilterSetLink}
                  setEditingFilterId={setEditingId}
                  openSubmenuAt={openSubmenuAt}
                  setOpenedFromPill={setOpenedFromPill}
                  onResetAll={handleResetAllFilters}
                  hasSelectionFilters={hasSelectionFilters}
                  appliedFilters={appliedFilters}
                  removeAppliedFilter={removeAppliedFilter}
                  show={hasAnyActiveFilter}
                  setIsFilterOpen={setIsFilterOpen}
                  setSecondaryFiltersDDL={setSecondaryFiltersDDL}
                  setSecondaryFiltersSource={setSecondaryFiltersSource}
                  setOptionsLoading={setOptionsLoading}
                  setIsDatePicker={setIsDatePicker}
                  filterTitle={filterTitle}
                  setFilterTitle={setFilterTitle}
                  secondFilterDisplay={secondFilterDisplay}
                  filterBy={filterBy}
                  setSecondFilterDisplay={setSecondFilterDisplay}
                  setFilterBy={setFilterBy}
                  filterArray={filterArray}
                  onOpen={handleOpenSaveFilterSet}
                  isSaved={isFilterSetSaved}
                  savedFilterKey={savedFilterKey}
                  className={isShipmentToolbarFilter ? 'xl-d-block' : ''}
                />
              )}
            </Flex>
          </Flex>
        </Col>
      </Row>

      {useInternalSaveFilterSetModal && (
        <ShipmentSaveFilterSetModal
          sentimentSelected={sentimentSelected}
          aiStateSelected={aiStateSelected}
          open={savePresetOpen}
          onClose={closeSavePresetModal}
          presets={presets}
          appliedFilters={appliedFilters}
          filterArray={filterArray}
          selectedPresetId={selectedPresetId}
          handleSavePreset={handleSavePreset}
          setLastSavedFiltersSignature={setLastSavedFiltersSignature}
        />
      )}

      <ViewFiltersModal
        open={viewFiltersModalOpen}
        tab={viewFiltersTab}
        onClose={handleCloseViewFiltersModal}
      />
    </>
  );
};

export default ShipmentFiltersPanelView;
